import config from "../config";
import { getUploadBrowserPreviewUrl, needsBrowserPreviewConversion } from "./imageFormats";

const MIN_IMAGE_WIDTH = 150;
const DEFAULT_BREAKPOINTS = [150, 240, 270, 320, 480, 768, 1280, 1920];

export const DEFAULT_USER_IMAGE_SRC = "/assets/default-user.png";

const useDevProxy =
	process.env.NODE_ENV === "development" && process.env.REACT_APP_API_USE_PROXY !== "false";

const toAbsoluteUrl = (relativePath) => {
	if (!relativePath) return "";
	if (/^https?:\/\//i.test(relativePath)) return relativePath;
	const normalizedPath = String(relativePath).replace(/^\/+/, "");
	if (useDevProxy) return `/${normalizedPath}`;
	return `${String(config.api.API_URL || "").replace(/\/+$/, "")}/${normalizedPath}`;
};

const pickClosestWidth = (availableWidths = [], targetWidth = 150) => {
	if (!availableWidths.length) return null;
	return availableWidths.reduce((prev, curr) =>
		Math.abs(curr - targetWidth) < Math.abs(prev - targetWidth) ? curr : prev
	);
};

const clampTargetWidth = (targetWidth = MIN_IMAGE_WIDTH) =>
	Math.max(Number(targetWidth) || MIN_IMAGE_WIDTH, MIN_IMAGE_WIDTH);

const toTimestamp = (value) => {
	if (!value) return 0;
	const timestamp = new Date(value).getTime();
	return Number.isFinite(timestamp) ? timestamp : 0;
};

const getObjectIdTimestamp = (value) => {
	const id = String(value || "");
	if (!/^[a-f\d]{24}$/i.test(id)) return 0;
	return Number.parseInt(id.slice(0, 8), 16) * 1000;
};

const getUploadTimestamp = (upload) =>
	Math.max(
		toTimestamp(upload?.createdAt),
		toTimestamp(upload?.date),
		getObjectIdTimestamp(upload?._id)
	);

const isActiveUpload = (upload) =>
	Boolean(upload) && upload.trashed !== true && upload.temporary !== true;

const normalizeAssetPath = (value) =>
	String(value || "")
		.replace(/\\/g, "/")
		.replace(/^\/+/, "")
		.trim();

export const isUploadAssetAvailable = (upload, relativePath) => {
	if (!Array.isArray(upload?.availablePaths)) return true;
	const normalizedPath = normalizeAssetPath(relativePath);
	return (
		Boolean(normalizedPath) &&
		upload.availablePaths.some(
			(availablePath) => normalizeAssetPath(availablePath) === normalizedPath
		)
	);
};

export const getUploadByDestiny = (entity, destiny = "image") => {
	const uploads = Array.isArray(entity?.uploads)
		? entity.uploads
		: entity?.uploads
			? [entity.uploads]
			: [];

	return uploads
		.filter((upload) => isActiveUpload(upload) && upload?.destiny === destiny)
		.reduce((latest, upload) => {
			if (!latest) return upload;
			return getUploadTimestamp(upload) >= getUploadTimestamp(latest) ? upload : latest;
		}, null);
};

const getUploadCacheVersion = (upload) =>
	upload?.optimizedAt || upload?.updatedAt || upload?.createdAt || upload?._id || "";

export const getUploadAssetSrc = (upload, relativePath) => {
	if (!isUploadAssetAvailable(upload, relativePath)) return "";
	const absoluteUrl = toAbsoluteUrl(relativePath);
	const version = getUploadCacheVersion(upload);
	if (!absoluteUrl || !version) return absoluteUrl;
	const separator = absoluteUrl.includes("?") ? "&" : "?";
	return `${absoluteUrl}${separator}v=${encodeURIComponent(String(version))}`;
};

export const getUploadImageSrc = (upload, targetWidth = 150) => {
	if (!upload) return "";
	const normalizedTargetWidth = clampTargetWidth(targetWidth);

	const variants = upload.variants;
	if (variants) {
		const variantPool = variants.avif || variants.webp || variants.jpeg || {};
		const widths = Object.keys(variantPool)
			.map((width) => Number(width))
			.filter(
				(width) =>
					Number.isFinite(width) &&
					width >= MIN_IMAGE_WIDTH &&
					isUploadAssetAvailable(upload, variantPool[width])
			);
		const nearestWidth = pickClosestWidth(widths, normalizedTargetWidth);

		if (nearestWidth && variantPool[nearestWidth]) {
			return getUploadAssetSrc(upload, variantPool[nearestWidth]);
		}
		if (variants.original && isUploadAssetAvailable(upload, variants.original)) {
			return getUploadAssetSrc(upload, variants.original);
		}
	}

	const fallbackPath = [upload.path, upload.pathThumb, upload.pathPlaceholder].find((candidate) =>
		isUploadAssetAvailable(upload, candidate)
	);
	return fallbackPath ? getUploadAssetSrc(upload, fallbackPath) : "";
};

/** Direct URL for video uploads (MP4 and other browser-playable formats). */
export const getUploadVideoSrc = (upload) => {
	if (!upload) return "";
	return getUploadAssetSrc(upload, upload.path);
};

/** Server-generated poster frame for a video, including the legacy pathThumb field. */
export const getUploadVideoPosterSrc = (upload) => {
	if (!upload) return "";
	return getUploadAssetSrc(upload, upload.posterPath || upload.pathThumb);
};

/** Prefer optimized variants; fall back to server JPEG preview for HEIC/HEIF uploads. */
export const getUploadDisplaySrc = (upload, targetWidth = 150) => {
	if (!upload) return "";
	const optimizedSrc = getUploadImageSrc(upload, targetWidth);
	if (!needsBrowserPreviewConversion(upload)) return optimizedSrc;
	const rawSrc = getUploadAssetSrc(upload, upload.path);
	if (!rawSrc) return optimizedSrc;
	if (optimizedSrc && rawSrc && optimizedSrc !== rawSrc) return optimizedSrc;
	return getUploadBrowserPreviewUrl(upload) || optimizedSrc;
};

/**
 * Source for a full-size viewer. Browser-native images use the uploaded file so
 * existing EXIF-oriented photos remain correct even if an older generated
 * variant had its orientation tag stripped. Formats browsers cannot decode are
 * served through the API's converted preview endpoint instead.
 */
export const getUploadOriginalDisplaySrc = (upload) => {
	if (!upload) return "";
	if (needsBrowserPreviewConversion(upload)) {
		const rawSrc = getUploadAssetSrc(upload, upload.path);
		return rawSrc
			? getUploadBrowserPreviewUrl(upload) || getUploadDisplaySrc(upload, 1920)
			: getUploadDisplaySrc(upload, 1920);
	}
	return getUploadAssetSrc(upload, upload.path) || getUploadDisplaySrc(upload, 1920);
};

export const getEntityImageSrc = (entity, { destiny = "image", targetWidth = 150 } = {}) => {
	const upload = getUploadByDestiny(entity, destiny);
	return getUploadDisplaySrc(upload, targetWidth);
};

/**
 * Resolve a person's avatar consistently across the application. Uploaded profile
 * images take priority, followed by legacy image fields, then the public default.
 */
export const getUserImageSrc = (user, options = {}) => {
	const uploadedImageSrc = getEntityImageSrc(user, options);
	if (uploadedImageSrc) return uploadedImageSrc;

	const legacyImageSrc = [user?.image, user?.photo, user?.avatarUrl, user?.avatar].find(
		(value) => typeof value === "string" && value.trim()
	);
	return legacyImageSrc || DEFAULT_USER_IMAGE_SRC;
};

export const getNearestKnownBreakpoint = (targetWidth = 150) =>
	pickClosestWidth(DEFAULT_BREAKPOINTS, clampTargetWidth(targetWidth)) || DEFAULT_BREAKPOINTS[0];
